/*
 * A single line of truncated text.
 * Truncation is in the middle of the text string
 * e.g A shortened version of "This is Kaleidoscope" would appear as "This is...scope".
 */

import React, { createRef } from "react";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import classNames from "classnames";
import { observer } from "mobx-react";
import { action, observable } from "mobx";

interface TruncatedTextProps {
  className?: string;
  children: string;
}

@observer
class TruncatedText extends React.Component<TruncatedTextProps> {
  @observable.ref
  private displayText = "";

  private containerRef = createRef<HTMLDivElement>();
  private originalTextRef = createRef<HTMLDivElement>();

  @action
  private measureAvailableSpace = () => {
    const text = this.props.children;
    const separator = "...";
    const containerWidth = this.containerRef.current?.getBoundingClientRect().width;
    const originalTextWidth = this.originalTextRef.current?.getBoundingClientRect().width;

    if (
      !!containerWidth &&
      !!originalTextWidth &&
      text.length > separator.length &&
      originalTextWidth > containerWidth
    ) {
      // Take a best guess the space available
      const avgCharSize = originalTextWidth / text.length;
      const maxLength = Math.floor(containerWidth / avgCharSize);
      const sliceStart = Math.ceil((maxLength - separator.length) / 2);
      const sliceEnd = text.length - sliceStart;

      this.displayText = `${text.slice(0, sliceStart)}${separator}${text.slice(sliceEnd, text.length)}`;
    } else {
      // Fallback to displaying the whole text
      this.displayText = text;
    }
  };

  public componentDidMount(): void {
    this.measureAvailableSpace();
  }

  public componentDidUpdate(prevProps: TruncatedTextProps): void {
    if (this.props.children !== prevProps.children) {
      this.measureAvailableSpace();
    }
  }

  public render(): JSX.Element {
    const { className, children } = this.props;

    /*
     * Render 3 copies of the text:
     * 1) Hidden, with original width based on the amount of text. This is used for calculating the original size.
     * 2) Hidden, this is used so the container will resize depending on the content within it, for when the text does not overflow.
     * 3) Display text, this is visible and centered in the middle, with any necessary truncation.
     */
    return (
      <ReactResizeDetector handleWidth handleHeight={false} onResize={this.measureAvailableSpace}>
        <div className="truncated-text" ref={this.containerRef}>
          <div className={classNames("truncated-text__hidden-text", className)} aria-hidden ref={this.originalTextRef}>
            {children}
          </div>
          <div className={classNames("truncated-text__ghost-text", className)} aria-hidden>
            {children}
          </div>
          <div className={classNames("truncated-text__display-text", className)}>{this.displayText}</div>
        </div>
      </ReactResizeDetector>
    );
  }
}

export default TruncatedText;
